"use client";

import { Wand2 } from "lucide-react";
import { useParams } from "next/navigation";
import { useState } from "react";
import { useToolStrings } from "@/components/citation-tool/tool-strings";
import { SaveCitationButton } from "@/components/save-citation-button";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { trackCitationCopied } from "@/lib/analytics";
import { copyRichText, writeToClipboardOrThrow } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

interface EditableEntry {
  title?: string;
  author?: string;
  issued?: string;
  URL?: string;
  publisher?: string;
  containerTitle?: string;
  type?: string;
  volume?: string;
  issue?: string;
  page?: string;
  DOI?: string;
  ISBN?: string;
}

interface CitationResultProps {
  citation: string;
  /**
   * The same reference as HTML, when the API returned it for this exact text. Copy then
   * puts both flavours on the clipboard, so Word and Google Docs keep the italics.
   */
  citationHtml?: string;
  /**
   * The matching in-text citation for the current style (issue #109): `(Smith, 2020)` for
   * author-date styles, `[1]` for IEEE, `(1)` for Vancouver. Omitted/empty → the line is
   * hidden, so older callers that don't compute it are unaffected.
   */
  inText?: string | null;
  entry: EditableEntry | null;
  style: string;
  lang: string;
  missingCount: number;
  onOpenWizard: () => void;
  onOpenEdit: () => void;
  onCitationSaved?: () => void;
  /** Inside the Studio the post-save "View in Studio" link would point at itself. */
  inStudio?: boolean;
}

export function CitationResult({
  citation,
  citationHtml,
  inText,
  entry,
  style,
  lang,
  missingCount,
  onOpenWizard,
  onOpenEdit,
  onCitationSaved,
  inStudio = false,
}: CitationResultProps) {
  const strings = useToolStrings();
  const params = useParams<{ locale?: string }>();
  const locale = params?.locale ?? "en";
  const [copied, setCopied] = useState(false);
  const [inTextCopied, setInTextCopied] = useState(false);

  async function handleCopy() {
    if (citationHtml) {
      if (!(await copyRichText(citationHtml, citation))) {
        throw new Error("Could not copy to the clipboard.");
      }
    } else {
      await writeToClipboardOrThrow(citation);
    }
    setCopied(true);
    trackCitationCopied(style);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleCopyInText() {
    if (!inText) {
      return;
    }
    await writeToClipboardOrThrow(inText);
    setInTextCopied(true);
    trackCitationCopied(style);
    setTimeout(() => setInTextCopied(false), 2000);
  }

  return (
    <Card className="animate-fade-in">
      <CardHeader className="flex flex-col items-stretch gap-4 space-y-0 pb-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <CardTitle className="flex flex-wrap items-center gap-2">
            {strings.resultTitle}
            {missingCount > 0 && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 font-normal text-amber-600 text-xs dark:bg-amber-900/30 dark:text-amber-400">
                {tPlural(strings.fieldsIncomplete, locale, missingCount)}
              </span>
            )}
          </CardTitle>
          <CardDescription>{strings.copyHint}</CardDescription>
        </div>
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:shrink-0">
          {entry && missingCount > 0 && (
            <Button
              className="border-amber-300 text-amber-600 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/20"
              onClick={onOpenWizard}
              size="sm"
              variant="outline"
            >
              <Wand2 className="size-4" />
              {strings.completeFields}
            </Button>
          )}
          {entry && (
            <Button onClick={onOpenEdit} size="sm" variant="outline">
              {strings.edit}
            </Button>
          )}
          {entry && citation && (
            <SaveCitationButton
              citation={citation}
              entry={entry}
              lang={lang}
              onSaved={onCitationSaved}
              showStudioLink={!inStudio}
              style={style}
            />
          )}
          <Button onClick={handleCopy} size="sm" variant="outline">
            {copied ? strings.copied : strings.copy}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed">
          {citation}
        </pre>
        {inText && (
          <div className="border-border border-t pt-4">
            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <div className="min-w-0">
                <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  {strings.inTextCitation}
                </p>
                <pre className="mt-1 whitespace-pre-wrap break-words font-sans text-sm leading-relaxed">
                  {inText}
                </pre>
              </div>
              <Button
                className="shrink-0 sm:self-start"
                onClick={handleCopyInText}
                size="sm"
                variant="outline"
              >
                {inTextCopied ? strings.copied : strings.copy}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
